import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  useTheme,
  keyframes,
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

const flowAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const LoadingMessage = () => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: { xs: 1, sm: 2 },
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, #4F46E5, #818CF8, #4F46E5)'
            : 'linear-gradient(90deg, #6366F1, #A5B4FC, #6366F1)',
          backgroundSize: '200% 100%',
          animation: `${flowAnimation} 2s linear infinite`,
        }
      }}
    >
      <Avatar
        sx={{
          bgcolor: 'primary.main',
          width: { xs: 28, sm: 32 },
          height: { xs: 28, sm: 32 },
        }}
      >
        <AutoAwesome sx={{ fontSize: { xs: 16, sm: 20 } }} />
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ 
          mb: 0.5, 
          color: 'text.secondary', 
          fontWeight: 500, 
          fontSize: { xs: '0.75rem', sm: '0.875rem' } 
        }}>
          AI Tutor
        </Typography>
        <Box sx={{ width: '100%' }}>
          {/* First line - longer */}
          <Box
            sx={{
              height: '12px',
              mb: 1.5,
              width: '90%',
              borderRadius: '4px',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}, 
                  transparent
                )`,
                animation: `${shimmerAnimation} 2s infinite linear`,
              }
            }}
          />
          {/* Second line - medium */}
          <Box
            sx={{
              height: '12px',
              mb: 1.5,
              width: '75%',
              borderRadius: '4px',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}, 
                  transparent
                )`,
                animation: `${shimmerAnimation} 2s infinite linear`,
                animationDelay: '0.2s',
              }
            }}
          />
          {/* Third line - shortest */}
          <Box
            sx={{
              height: '12px',
              width: '40%',
              borderRadius: '4px',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}, 
                  transparent
                )`,
                animation: `${shimmerAnimation} 2s infinite linear`,
                animationDelay: '0.4s',
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingMessage; 